import '../../syncfusionStyles';

import { useQuery, useMutation } from '@apollo/client';
import {
  CalendarMonth as CalendarIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Description as FileTextIcon,
  Search as SearchIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {
  Annotation,
  AnnotationSettingsModel,
  BookmarkView,
  FormDesigner,
  FormFields,
  Inject,
  LinkAnnotation,
  Magnification,
  Navigation,
  PdfViewerComponent,
  Print,
  TextSearch,
  TextSelection,
  ThumbnailView,
  Toolbar,
} from '@syncfusion/ej2-react-pdfviewer';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { GET_COMPANY_ANNOTATIONS, SAVE_ANNOTATION, DELETE_ANNOTATION } from '../../graphql/types/annotation';
import { useSnackbar } from '../../providers/SnackbarProvider';

interface AnnotationData {
  area?: string;
  category1?: string;
  category2?: string;
  category3?: string;
  question?: string;
  displayText?: string;
  categoryPath?: string;
}

interface CompanyAnnotation {
  dbId: number;
  uploadFileId: number;
  text: string;
  annotationPageNumber?: number;
  xLocation?: number;
  yLocation?: number;
  datetimeCreated: string;
  datetimeModified: string;
}

const CompanyAnnotationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState('');
  const [fileFilter, setFileFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // PDF Viewer state
  const [pdfFileUrl, setPdfFileUrl] = useState<string | null>(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState<CompanyAnnotation | null>(null);

  // Add category selection state
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedCategory1, setSelectedCategory1] = useState('');
  const [selectedCategory2, setSelectedCategory2] = useState('');
  const [selectedCategory3, setSelectedCategory3] = useState('');

  const clearExistingAnnotations = () => {
    const container = document.querySelector('#annotation-pdf-viewer .e-pv-viewer-container');
    if (container) {
      let clearedCount = 0;
      const dbOverlays = container.querySelectorAll('div[data-db-id]');
      dbOverlays.forEach(overlay => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
          clearedCount++;
        }
      });
      const colorOverlays = container.querySelectorAll('div[style*="background-color: rgb(255, 255, 204)"]');
      colorOverlays.forEach(overlay => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
          clearedCount++;
        }
      });
      const buttonOverlays = container.querySelectorAll('div button[style*="background: rgb(255, 68, 68)"]');
      buttonOverlays.forEach(button => {
        const overlay = button.closest('div[style*="position: absolute"]');
        if (overlay && overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
          clearedCount++;
        }
      });
      // Also clear annotation highlights
      const existingHighlights = container.querySelectorAll('.annotation-highlight');
      existingHighlights.forEach(highlight => {
        if ((highlight as any).__cleanup) {
          (highlight as any).__cleanup();
        }
        highlight.remove();
      });
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const companyId = queryParams.get('companyId');

  const { data, loading, error, refetch } = useQuery(GET_COMPANY_ANNOTATIONS, {
    variables: { companyId: parseInt(companyId || '0') },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
  });

  const [saveAnnotation] = useMutation(SAVE_ANNOTATION, {
    onCompleted: () => {
      showSnackbar(t('companyAnnotations.annotationSaved'), 'success');
    },
    onError: error => {
      console.error('Error saving annotation:', error);
      showSnackbar(t('companyAnnotations.errorSavingAnnotation'), 'error');
    },
  });

  const [deleteAnnotation] = useMutation(DELETE_ANNOTATION, {
    onCompleted: () => {
      showSnackbar(t('companyAnnotations.annotationDeleted'), 'success');
      closePdfViewer();

      setTimeout(() => {
        refetch();
      }, 500);
    },
    onError: error => {
      console.error('Error deleting annotation:', error);
      showSnackbar(t('companyAnnotations.errorDeletingAnnotation'), 'error');
    },
  });

  // Fetch categories for annotation creation
  const { data: categoriesData } = useQuery(require('../../graphql/types/annotation').GET_PDF_CATEGORIES, {
    onError: error => {
      console.error('Error loading categories:', error);
      showSnackbar(t('companyFilesPage.categoriesLoadError'), 'error', false);
    },
  });

  const rawCategories = categoriesData?.pdfDocumentAnnotationsMenuCategories || [];
  const allAreas = rawCategories.map(item => item.area);
  const areas = allAreas.filter((area, index) => allAreas.indexOf(area) === index);
  const categories1 = selectedArea
    ? rawCategories
        .filter(cat => cat.area === selectedArea)
        .map(cat => cat.category1)
        .filter((cat, idx, arr) => arr.indexOf(cat) === idx)
    : [];
  const categories2 = selectedCategory1
    ? rawCategories
        .filter(cat => cat.area === selectedArea && cat.category1 === selectedCategory1)
        .map(cat => cat.category2)
        .filter(Boolean)
        .filter((cat, idx, arr) => arr.indexOf(cat) === idx)
    : [];
  const categories3 = selectedCategory2
    ? rawCategories
        .filter(
          cat => cat.area === selectedArea && cat.category1 === selectedCategory1 && cat.category2 === selectedCategory2
        )
        .map(cat => cat.category3)
        .filter(Boolean)
        .filter((cat, idx, arr) => arr.indexOf(cat) === idx)
    : [];
  const currentQuestion =
    selectedArea && selectedCategory1
      ? rawCategories.find(
          item =>
            item.area === selectedArea &&
            item.category1 === selectedCategory1 &&
            (item.category2 === selectedCategory2 || (!item.category2 && !selectedCategory2)) &&
            (item.category3 === selectedCategory3 || (!item.category3 && !selectedCategory3))
        )?.question
      : '';

  const annotations: CompanyAnnotation[] = useMemo(() => {
    return data?.getAllPdfDocumentAnnotations?.edges?.map((edge: any) => edge.node) || [];
  }, [data]);

  const parseAnnotationData = (text: string): AnnotationData => {
    try {
      return JSON.parse(text);
    } catch {
      return { displayText: text };
    }
  };

  const filteredAnnotations = useMemo(() => {
    return annotations.filter(annotation => {
      const annotationData = parseAnnotationData(annotation.text);
      const searchInText = [
        annotationData.displayText || '',
        annotationData.area || '',
        annotationData.category1 || '',
        annotationData.question || '',
        `File ID: ${annotation.uploadFileId}`,
      ]
        .join(' ')
        .toLowerCase();

      const matchesSearch = searchTerm === '' || searchInText.includes(searchTerm.toLowerCase());
      const matchesFile = fileFilter === '' || annotation.uploadFileId.toString().includes(fileFilter);
      const matchesCategory =
        categoryFilter === '' || (annotationData.area && annotationData.area.includes(categoryFilter));

      return matchesSearch && matchesFile && matchesCategory;
    });
  }, [annotations, searchTerm, fileFilter, categoryFilter]);

  const uniqueFiles = useMemo(() => {
    const files = new Set();
    annotations.forEach(annotation => {
      files.add(annotation.uploadFileId.toString());
    });
    return Array.from(files) as string[];
  }, [annotations]);

  const uniqueCategories = useMemo(() => {
    const categories = new Set();
    annotations.forEach(annotation => {
      const annotationData = parseAnnotationData(annotation.text);
      if (annotationData.area) {
        categories.add(annotationData.area);
      }
    });
    return Array.from(categories) as string[];
  }, [annotations]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setFileFilter('');
    setCategoryFilter('');
  };

  const getAnnotationDisplayText = (annotation: CompanyAnnotation): string => {
    const annotationData = parseAnnotationData(annotation.text);
    return annotationData.displayText || annotationData.categoryPath || annotation.text;
  };

  const getAnnotationCategory = (annotation: CompanyAnnotation): string => {
    const annotationData = parseAnnotationData(annotation.text);
    if (annotationData.area && annotationData.category1) {
      return `${annotationData.area} > ${annotationData.category1}`;
    }
    return annotationData.area || 'Uncategorized';
  };

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const handleAnnotationClick = async (annotation: CompanyAnnotation) => {
    try {
      const hostUrl = process.env.REACT_APP_FILESERVER_BACKEND;
      // Construct PDF URL directly using file ID (backend proxy endpoint)
      // This matches the pattern from UploadFile.get_drive_url() for PDFs
      const pdfUrl = `${hostUrl}/file/${annotation.uploadFileId}/download`;

      setSelectedAnnotation(annotation);
      setPdfFileUrl(pdfUrl);
    } catch (error) {
      console.error('Error loading file:', error);
      showSnackbar('Error loading file', 'error');
    }
  };

  const closePdfViewer = () => {
    // Clean up any existing annotation overlays and their event listeners
    const existingHighlights = document.querySelectorAll('.annotation-highlight');
    existingHighlights.forEach(highlight => {
      if ((highlight as any).__cleanup) {
        (highlight as any).__cleanup();
      }
      highlight.remove();
    });

    setPdfFileUrl(null);
    setSelectedAnnotation(null);
  };

  const handlePdfViewerDocumentLoad = () => {
    if (selectedAnnotation?.annotationPageNumber) {
      setTimeout(() => {
        const pdfViewer = document.getElementById('annotation-pdf-viewer') as any;
        if (pdfViewer?.ej2_instances?.[0]) {
          pdfViewer.ej2_instances[0].navigation.goToPage(selectedAnnotation.annotationPageNumber);

          if (selectedAnnotation.xLocation && selectedAnnotation.yLocation) {
            setTimeout(() => {
              addAnnotationHighlight(
                selectedAnnotation.xLocation!,
                selectedAnnotation.yLocation!,
                getAnnotationDisplayText(selectedAnnotation),
                selectedAnnotation.dbId
              );
            }, 1000);
          }
        }
      }, 500);
    }
  };

  const addAnnotationHighlight = (x: number, y: number, text: string, dbId: number) => {
    // Clean up existing highlights and their event listeners
    const existingHighlights = document.querySelectorAll('.annotation-highlight');
    existingHighlights.forEach(highlight => {
      if ((highlight as any).__cleanup) {
        (highlight as any).__cleanup();
      }
      highlight.remove();
    });

    const container = document.querySelector('#annotation-pdf-viewer .e-pv-viewer-container');
    if (!container) return;

    // Create DOM overlay
    const overlay = document.createElement('div');
    overlay.className = 'annotation-highlight';
    overlay.style.position = 'absolute';
    overlay.style.left = `${x}px`;
    overlay.style.top = `${y}px`;
    overlay.style.transform = 'translate(-50%, -50%)';
    overlay.style.backgroundColor = '#FFFFCC';
    overlay.style.border = '1px solid #000';
    overlay.style.padding = '8px';
    overlay.style.fontSize = '12px';
    overlay.style.zIndex = '1000';
    overlay.style.pointerEvents = 'auto';
    overlay.style.borderRadius = '4px';
    overlay.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
    overlay.style.fontFamily = 'Arial, sans-serif';
    overlay.style.cursor = 'move';
    overlay.style.userSelect = 'none';
    overlay.style.maxWidth = '200px';
    overlay.style.wordBreak = 'break-word';

    if (dbId) {
      overlay.setAttribute('data-db-id', dbId.toString());
    }

    overlay.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <span>${text}</span>
        <button style="
          background: #ff4444; 
          color: white; 
          border: none; 
          border-radius: 3px; 
          width: 20px; 
          height: 20px; 
          cursor: pointer; 
          font-size: 12px;
          margin-left: 10px;
        ">×</button>
      </div>
    `;

    const closeButton = overlay.querySelector('button');
    if (closeButton) {
      closeButton.addEventListener('click', async () => {
        if (dbId) {
          try {
            await deleteAnnotation({
              variables: { annotationId: parseInt(dbId.toString()) },
            });
          } catch (error) {
            console.error('Failed to delete annotation from backend:', error);
          }
        }

        // Clean up event listeners before removing the overlay
        if ((overlay as any).__cleanup) {
          (overlay as any).__cleanup();
        }

        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      });
    }

    let isDragging = false;
    let offsetX, offsetY;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.target !== closeButton) {
        isDragging = true;
        const rect = overlay.getBoundingClientRect();
        offsetX = e.clientX - (rect.left + rect.width / 2);
        offsetY = e.clientY - (rect.top + rect.height / 2);
        e.preventDefault();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const containerRect = container.getBoundingClientRect();
        const newCenterX = e.clientX - offsetX - containerRect.left;
        const newCenterY = e.clientY - offsetY - containerRect.top;
        overlay.style.left = newCenterX + 'px';
        overlay.style.top = newCenterY + 'px';
      }
    };

    const handleMouseUp = async () => {
      if (isDragging && dbId && selectedAnnotation) {
        const rect = overlay.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const relativeX = Math.round(rect.left + rect.width / 2 - containerRect.left);
        const relativeY = Math.round(rect.top + rect.height / 2 - containerRect.top);

        try {
          const overlayText = overlay.querySelector('span')?.textContent || text;

          let annotationData;
          try {
            if (overlayText.includes(' > ')) {
              const parts = overlayText.split(' > ');
              annotationData = {
                area: parts[0],
                category1: parts[1] || parts[0],
                displayText: overlayText,
              };
            } else {
              annotationData = { displayText: overlayText };
            }
          } catch (parseError) {
            annotationData = { displayText: overlayText };
          }

          const viewer = document.getElementById('annotation-pdf-viewer') as any;
          const currentPage =
            viewer?.ej2_instances?.[0]?.currentPageNumber || selectedAnnotation.annotationPageNumber || 1;

          await saveAnnotation({
            variables: {
              pdfDocumentAnnotationId: parseInt(dbId.toString()),
              uploadFileId: selectedAnnotation.uploadFileId,
              authorId: 1,
              text: JSON.stringify(annotationData),
              annotationPageNumber: currentPage,
              xLocation: relativeX,
              yLocation: relativeY,
            },
          });

          // Update the selectedAnnotation state with the new position
          setSelectedAnnotation(prev =>
            prev
              ? {
                  ...prev,
                  xLocation: relativeX,
                  yLocation: relativeY,
                  annotationPageNumber: currentPage,
                }
              : null
          );
        } catch (error) {
          console.error('Failed to save position for annotation', dbId, ':', error);
        }
      }
      isDragging = false;
    };

    overlay.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    const cleanupEventListeners = () => {
      overlay.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    (overlay as any).__cleanup = cleanupEventListeners;

    container.appendChild(overlay);
  };

  const addCategoryAnnotation = async () => {
    if (!selectedArea || !selectedCategory1 || !currentQuestion || !selectedAnnotation) return;
    const viewer = document.getElementById('annotation-pdf-viewer') as any;
    if (!viewer?.ej2_instances?.[0]) return;
    const pdfViewer = viewer.ej2_instances[0];
    const categoryPath = [selectedArea, selectedCategory1, selectedCategory2, selectedCategory3]
      .filter(Boolean)
      .join(' > ');
    const lastCategory = selectedCategory3 || selectedCategory2 || selectedCategory1;
    const subjectText = `${selectedArea} > ${lastCategory}`;
    const currentPage = pdfViewer.currentPageNumber;
    let pageWidth = 612;
    let pageHeight = 792;
    try {
      const pageElement = document.querySelector(
        `#annotation-pdf-viewer .e-pv-page-div[data-page-number="${currentPage}"]`
      );
      if (pageElement) {
        pageWidth = pageElement.clientWidth;
        pageHeight = pageElement.clientHeight;
      }
    } catch (error) {
      console.log('Could not get page dimensions, using defaults');
    }
    const centerX = pageWidth / 2;
    const centerY = pageHeight / 2;
    const annotationData = {
      area: selectedArea,
      category1: selectedCategory1,
      category2: selectedCategory2,
      category3: selectedCategory3,
      question: currentQuestion,
      displayText: subjectText,
      categoryPath: categoryPath,
    };
    try {
      const { data: mutationData } = await saveAnnotation({
        variables: {
          uploadFileId: selectedAnnotation.uploadFileId,
          authorId: 1,
          text: JSON.stringify(annotationData),
          annotationPageNumber: currentPage,
          xLocation: centerX,
          yLocation: centerY,
        },
      });
      let newDbId = null;
      if (
        mutationData &&
        mutationData.savePdfDocumentAnnotation &&
        mutationData.savePdfDocumentAnnotation.pdfDocumentAnnotation
      ) {
        newDbId = mutationData.savePdfDocumentAnnotation.pdfDocumentAnnotation.dbId;
      }
      setSelectedAnnotation({
        dbId: newDbId || Date.now(),
        uploadFileId: selectedAnnotation.uploadFileId,
        text: JSON.stringify(annotationData),
        annotationPageNumber: currentPage,
        xLocation: centerX,
        yLocation: centerY,
        datetimeCreated: new Date().toISOString(),
        datetimeModified: new Date().toISOString(),
      });
      setTimeout(() => {
        clearExistingAnnotations();
        addAnnotationHighlight(centerX, centerY, subjectText, newDbId || Date.now());
      }, 500);
      setTimeout(() => {
        refetch();
      }, 500);
    } catch (error) {
      console.error('Error saving annotation to backend:', error);
      showSnackbar('Error saving annotation', 'error', false);
      return;
    }
    pdfViewer.annotationModule.setAnnotationMode('None');
    setTimeout(() => {
      pdfViewer.annotationModule.setAnnotationMode('None');
    }, 100);
    setSelectedArea('');
    setSelectedCategory1('');
    setSelectedCategory2('');
    setSelectedCategory3('');
  };

  if (!companyId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('companyAnnotations.companyIdRequired')}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('companyAnnotations.errorLoadingAnnotations')}: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('companyAnnotations.companyAnnotations')}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('companyAnnotations.searchAnnotations')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('companyAnnotations.filterByFile')}</InputLabel>
                <Select
                  value={fileFilter}
                  label={t('companyAnnotations.filterByFile')}
                  onChange={e => setFileFilter(e.target.value)}
                >
                  <MenuItem value="">{t('companyAnnotations.allFiles')}</MenuItem>
                  {uniqueFiles.map(fileName => (
                    <MenuItem key={fileName} value={fileName}>
                      {fileName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('companyAnnotations.filterByCategory')}</InputLabel>
                <Select
                  value={categoryFilter}
                  label={t('companyAnnotations.filterByCategory')}
                  onChange={e => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">{t('companyAnnotations.allCategories')}</MenuItem>
                  {uniqueCategories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Tooltip title={t('companyAnnotations.clearAllFilters')}>
                <IconButton onClick={handleClearFilters} sx={{ height: '56px', width: '100%' }}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {t('companyAnnotations.annotations')} ({filteredAnnotations.length} {t('companyAnnotations.of')}{' '}
              {annotations.length})
            </Typography>
          </Box>

          {filteredAnnotations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                {t('companyAnnotations.noAnnotationsFound')}
                {searchTerm || fileFilter || categoryFilter
                  ? t('companyAnnotations.matchingYourFilters')
                  : t('companyAnnotations.forThisCompany')}
                .
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '600px' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('companyAnnotations.annotation')}</TableCell>
                    <TableCell>{t('companyAnnotations.category')}</TableCell>
                    <TableCell>{t('companyAnnotations.file')}</TableCell>
                    <TableCell>{t('companyAnnotations.page')}</TableCell>
                    <TableCell>{t('companyAnnotations.created')}</TableCell>
                    <TableCell>{t('companyAnnotations.modified')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAnnotations.map(annotation => (
                    <TableRow key={annotation.dbId} hover>
                      <TableCell>
                        <Box sx={{ maxWidth: '300px' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: 'break-word',
                              cursor: 'pointer',
                              color: 'primary.main',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                            onClick={() => handleAnnotationClick(annotation)}
                          >
                            {getAnnotationDisplayText(annotation)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<TagIcon />}
                          label={getAnnotationCategory(annotation)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FileTextIcon fontSize="small" />
                          <Typography variant="body2" noWrap sx={{ maxWidth: '200px' }}>
                            File ID: {annotation.uploadFileId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {annotation.annotationPageNumber
                          ? `${t('companyAnnotations.page')} ${annotation.annotationPageNumber}`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon fontSize="small" />
                          <Typography variant="body2">{formatDate(annotation.datetimeCreated)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(annotation.datetimeModified)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* PDF Viewer Dialog */}
      <Dialog open={!!pdfFileUrl} onClose={closePdfViewer} maxWidth="lg" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6">
            {selectedAnnotation && `Annotation: ${getAnnotationDisplayText(selectedAnnotation)}`}
            {selectedAnnotation?.annotationPageNumber && ` (Page ${selectedAnnotation.annotationPageNumber})`}
          </Typography>
          <IconButton onClick={closePdfViewer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>{t('companyFilesPage.area')}</InputLabel>
              <Select
                value={selectedArea}
                onChange={e => {
                  setSelectedArea(e.target.value);
                  setSelectedCategory1('');
                  setSelectedCategory2('');
                  setSelectedCategory3('');
                }}
                label="Area"
              >
                {areas.map(area => (
                  <MenuItem key={area} value={area}>
                    {area}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedArea && (
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>{t('companyFilesPage.category1')}</InputLabel>
                <Select
                  value={selectedCategory1}
                  onChange={e => {
                    setSelectedCategory1(e.target.value);
                    setSelectedCategory2('');
                    setSelectedCategory3('');
                  }}
                  label={t('companyFilesPage.category1')}
                >
                  {categories1.map(cat => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {selectedCategory1 && (
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>{t('companyFilesPage.category2')}</InputLabel>
                <Select
                  value={selectedCategory2}
                  onChange={e => {
                    setSelectedCategory2(e.target.value);
                    setSelectedCategory3('');
                  }}
                  label={t('companyFilesPage.category2')}
                >
                  {categories2.map(cat => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {selectedCategory2 && categories3.length > 0 && (
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>{t('companyFilesPage.category3')}</InputLabel>
                <Select
                  value={selectedCategory3}
                  onChange={e => setSelectedCategory3(e.target.value)}
                  label={t('companyFilesPage.category3')}
                >
                  {categories3.map(cat => (
                    <MenuItem key={cat} value={cat}>
                      <Tooltip title={currentQuestion}>
                        <span>{cat}</span>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="contained"
              onClick={addCategoryAnnotation}
              disabled={!currentQuestion}
              sx={{
                backgroundColor: currentQuestion ? '#4CAF50' : undefined,
                '&:hover': { backgroundColor: currentQuestion ? '#45a049' : undefined },
                minWidth: '160px',
                fontWeight: 'bold',
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: currentQuestion ? '0 2px 4px rgba(0,0,0,0.2)' : undefined,
              }}
            >
              {t('companyFilesPage.addAnnotation')}
            </Button>
            {currentQuestion && (
              <Tooltip title={currentQuestion} placement="top" arrow>
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        {pdfFileUrl && (
          <PdfViewerComponent
            id="annotation-pdf-viewer"
            documentPath={pdfFileUrl}
            resourceUrl="https://cdn.syncfusion.com/ej2/24.1.41/dist/ej2-pdfviewer-lib"
            style={{ height: '640px' }}
            enableAnnotation={true}
            documentLoad={handlePdfViewerDocumentLoad}
            annotationSettings={
              {
                author: 'User',
                minimumZoom: 0.5,
                maximumZoom: 3.0,
                isLock: false,
                isPrint: true,
                annotationMode: 'None',
              } as AnnotationSettingsModel
            }
          >
            <Inject
              services={[
                Toolbar,
                Magnification,
                Navigation,
                Annotation,
                LinkAnnotation,
                BookmarkView,
                ThumbnailView,
                Print,
                TextSelection,
                TextSearch,
                FormFields,
                FormDesigner,
              ]}
            />
          </PdfViewerComponent>
        )}
      </Dialog>
    </Box>
  );
};

export default CompanyAnnotationsPage;
